/* global atlas */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import LoadingIndicator from '../LoadingIndicator';
import ErrorNotification from '../common/ErrorNotification';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

const { REACT_APP_AZURE_MAP_KEY } = process.env;
const styles = () => ({
  mapContainer: {
    display: 'flex',
    width: '100%',
    height: '100%'
  },
  map: {
    minWidth: '600px',
    minHeight: '500px',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  }
});
const mapName = 'shipmentDetails';

const LONGITUDE = 0;
const LATITUDE = 1;

export class ShipmentMap extends Component {
  static propTypes = {
    shipment: PropTypes.object.isRequired
  };
  state = {
    mapLoaded: false,
    errors: null
  };
  async componentDidMount() {
    const { shipment, t } = this.props;

    atlas.setSubscriptionKey(REACT_APP_AZURE_MAP_KEY);

    const addressErrors = [];
    const originAddress = shipment.pickup.locationAddress;
    const destinationAddress = shipment.delivery.locationAddress;
    let originCoordinates = [];
    let destinationCoordinates = [];

    if (!originAddress.long) {
      addressErrors.push(t('Pickup'));
    } else {
      originCoordinates = [originAddress.long, originAddress.lat];
    }

    if (!destinationAddress.long) {
      addressErrors.push(t('Delivery'));
    } else {
      destinationCoordinates = [
        destinationAddress.long,
        destinationAddress.lat
      ];
    }

    if (addressErrors.length > 0) {
      this.setState({
        errors: `${t('Cannot locate map addresses for')} ${addressErrors.join(
          ', '
        )}`
      });
    } else {
      this.loadMap(originCoordinates, destinationCoordinates);
    }
  }
  loadMap = (pickup, delivery) => {
    const { t } = this.props;
    const component = this;
    const center = pickup;
    const map = new atlas.Map(mapName, {
      center,
      zoom: 4
    });

    //Wait until the map resources are ready.
    map.events.add('ready', function() {
      const zoomControl = new atlas.control.ZoomControl();
      map.controls.add(zoomControl, {
        position: 'top-right'
      });

      map.resize();
      map.events.add('load', function() {
        const datasource = new atlas.source.DataSource();
        map.sources.add(datasource);
        const points = [];

        points.push({
          point: pickup,
          label: t('Pickup')
        });
        points.push({
          point: delivery,
          label: t('Delivery')
        });

        points.forEach(mapPoint => {
          const htmlMarker = new atlas.data.Feature(
            new atlas.data.Point(mapPoint.point),
            {
              label: mapPoint.label
            }
          );

          datasource.add(htmlMarker);
          component.setState({ mapLoaded: true });
        });

        const layers = [
          new atlas.layer.SymbolLayer(datasource, null, {
            filter: ['==', '$type', 'Point']
          })
        ];

        map.resize();

        const allPoints = points.map(point => {
          return point.point;
        });
        datasource.add(
          new atlas.data.Feature(new atlas.data.LineString(allPoints), {
            type: 'Private Road'
          })
        );

        layers.push(
          new atlas.layer.LineLayer(datasource, null, {
            strokeWidth: 2,
            strokeColor: 'blue',
            filter: ['==', '$type', 'LineString']
          })
        );

        map.layers.add(layers);
      });

      /* Construct and add a style control (satellite view, etc.)*/
      const styleControl = new atlas.control.StyleControl({
        //Optionally specify which map styles you want to appear in the picker.
        //All styles available with the S0 license tier appear by default in the control.
        //If using a S1 tier license, you can use the mapStyles option to add the 'satellite' and 'satellite_road_labels' styles to the control.
        mapStyles: [
          'road',
          'grayscale_dark',
          'night',
          'road_shaded_relief',
          'satellite',
          'satellite_road_labels'
        ]
      });
      map.controls.add(styleControl, {
        position: 'bottom-right'
      });

      /* Fit map window to the bounding box defined by the start and delivery points */
      const swLon = Math.min(pickup[LONGITUDE], delivery[LONGITUDE]);
      const swLat = Math.min(pickup[LATITUDE], delivery[LATITUDE]);
      const neLon = Math.max(pickup[LONGITUDE], delivery[LONGITUDE]);
      const neLat = Math.max(pickup[LATITUDE], delivery[LATITUDE]);
      map.setCamera({
        bounds: [swLon, swLat, neLon, neLat],
        padding: 35
      });
    });
  };

  render() {
    const { classes } = this.props;
    const { errors, mapLoaded } = this.state;
    if (!errors) {
      return (
        <Paper className={classes.mapContainer}>
          <div id={mapName} className={classes.map} />
          {!mapLoaded && <LoadingIndicator />}
        </Paper>
      );
    } else {
      return <ErrorNotification message={this.state.errors} />;
    }
  }
}
const styledShipmentMap = withStyles(styles)(ShipmentMap);
export default withNamespaces()(styledShipmentMap);
